import { api } from "./api";
import {
  ListRequest,
  ListRequestSchemaV2,
  MessageResponse,
  PaginatedResponse,
} from "../types/common/commonTypes";
import {
  InvMovBatchCreate,
  InvMovBatchFull,
  InvMovCreate,
  InvMovFulfillment,
  InvMovFull,
  InvMovUpdate,
  PaginatedInvMovResponse,
} from "../types/invMovementTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const mov_base = "/inventory/movement";
export const invMovementService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: InvMovCreate): Promise<InvMovFull> => {
    const response = await api.post(`${mov_base}/`, data);
    return response.data;
  },
  batchCreate: async (data: InvMovBatchCreate): Promise<InvMovBatchFull> => {
    const response = await api.post(`${mov_base}/batch`, data);
    return response.data;
  },
  list: async (params: ListRequest): Promise<PaginatedInvMovResponse> => {
    const response = await api.post(`${mov_base}/search`, params);
    return response.data;
  },
  listFulfillments: async (
    params: ListRequestSchemaV2,
  ): Promise<PaginatedResponse<InvMovFulfillment>> => {
    const response = await api.post(`${mov_base}/fulfillments`, params);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<InvMovFull> => {
    const response = await api.get(`${mov_base}/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (id: string, data: InvMovUpdate): Promise<InvMovFull> => {
    const response = await api.put(`${mov_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${mov_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<InvMovFull> => {
    const response = await api.post(`${mov_base}/${id}/restore`);
    return response.data;
  },
};
